<template>
  <div>
    <v-row no-gutters>
      <v-spacer />
      <load-builder-template @input="templateSetter" />
      <save-builder-template :template-getter="templateGetter" />
    </v-row>
    <v-row class="my-6" no-gutters>
      <v-col data-userflow-id="email-editor" cols="12">
        <email-editor
          ref="emailEditor"
          :appearance="appearance"
          :project-id="projectId"
          :locale="locale"
          :tools="customTools"
          :options="options"
          @load="editorLoaded"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { EmailEditor } from 'vue-email-editor'
import isEmpty from 'lodash/isEmpty'
import { characterVariables } from '@/mixins/character-variables'
import { getOfferCardCustomJS, extractBestOfferOrder, isEditMode } from './OfferCardConfig'
import { getAutoDistributedOfferCardCustomJS, extractBestOfferOrder as extractAutoDistributedBestOfferOrder } from './AutoDistributedCardConfig'
import { bestOfferValidations } from '@/modules/maria/campanha-da-maria-v2/mixins/best-offer-validations'

export default {
  name: 'BuilderEmailComposer',
  components: {
    EmailEditor,
    LoadBuilderTemplate: () => import('@/modules/campanhas/campanhas-email/views/components/builder-templates/LoadBuilderTemplate.vue'),
    SaveBuilderTemplate: () => import('@/modules/campanhas/campanhas-email/views/components/builder-templates/SaveBuilderTemplate.vue'),
  },
  mixins: [characterVariables, bestOfferValidations],
  props: {
    message: { type: Object, default: () => ({}) },
    campaignData: { type: Object, default: () => ({}) },
    defaultColors: { type: Object, default: () => ({}) },
    subject: { type: String, default: '' },
  },
  data: () => ({
    localBuilderConfiguration: null,
    localProcessedHtml: null,
    productsWithImage: 0,

    offerCardGlobalColors: { mecanicaColor: null, descontoColor: null },
    offerCardGlobalFont: null,

    offerCardKnownIds: new Set(),
    offerCardLastState: new Map(),

    offerCardUpdateTimer: null,
    isConfiguringOfferCards: false,
    configureOfferCardsTimeout: null,
    editorListenersSetup: false,
  }),
  computed: {
    appearance() {
      return {
        theme: 'dark',
        panels: { tools: { dock: 'right' } },
      }
    },
    projectId() {
      return 203339
    },
    locale() {
      return 'pt-BR'
    },
    defaultTools() {
      return { image: { enabled: true } }
    },
    modelQuantityMap() {
      const map = {}
      const maxBestOfferModelClients = this.campaignData?.max_best_offer_model_clients

      if (!maxBestOfferModelClients || typeof maxBestOfferModelClients !== 'object' || Array.isArray(maxBestOfferModelClients)) {
        return map
      }

      ;['repurchase', 'recovery', 'activation'].forEach((key) => {
        const raw = maxBestOfferModelClients?.[key]
        const value = Number(raw)
        if (raw !== null && raw !== undefined && Number.isFinite(value) && value > 0) {
          map[key] = value
        }
      })

      return map
    },
    bestOfferOptions() {
      if (this.campaignData?.auto_distributed === true) {
        const maxQuantity = Math.max(...Object.values(this.modelQuantityMap || {}), 0)
        const quantity = Math.min(maxQuantity, 10)
        const options = []
        for (let i = 1; i <= quantity; i++) {
          options.push({ label: `${i}ª Melhor Oferta`, value: i.toString() })
        }
        return options
      }

      const count = Math.min(this.productsWithImage || 0, 10)
      const options = []
      for (let i = 1; i <= count; i++) {
        options.push({ label: `${i}ª Melhor Oferta`, value: i.toString() })
      }
      return options
    },
    modelOptions() {
      const modelMapping = {
        repurchase: { value: 'repurchase', label: 'Recompra' },
        recovery: { value: 'recovery', label: 'Resgate' },
        activation: { value: 'activation', label: 'Ativação' },
      }

      const maxBestOfferModelClients = this.campaignData?.max_best_offer_model_clients
      if (!maxBestOfferModelClients || typeof maxBestOfferModelClients !== 'object' || Array.isArray(maxBestOfferModelClients)) {
        return []
      }

      return Object.keys(modelMapping)
        .filter((key) => {
          const raw = maxBestOfferModelClients?.[key]
          const value = Number(raw)
          return raw !== null && raw !== undefined && Number.isFinite(value) && value > 0
        })
        .map((key) => modelMapping[key])
        .sort((a, b) => a.value.localeCompare(b.value))
    },
    defaultModelValue() {
      if (this.modelOptions.length === 0) {
        return 'activation'
      }
      return this.modelOptions[0].value
    },
    customTools() {
      const offerCardProperties = {
        bestOffer: { editor: { data: { options: this.bestOfferOptions } } },
        templateSize: {
          editor: {
            data: {
              options: [
                { label: 'Vertical', value: 'small' },
                { label: 'Horizontal', value: 'large' },
              ],
            },
          },
        },
      }

      if (this.campaignData?.auto_distributed === true) {
        offerCardProperties.model = {
          editor: {
            data: {
              options: this.modelOptions,
            },
          },
        }
      }

      return {
        ...this.defaultTools,
        'custom#offer_card': { properties: offerCardProperties },
      }
    },
    bestItemsVariables() {
      if (!this.isMariaCampaign) return []
      const label = `${this.getBestProductText}, do dia {inicio validade} ate {fim validade}]`
      const variableInfo = {
        description: '- Exibe, dos produtos em promoção, qual o consumidor tem mais chance de comprar seguindo os critérios incluso na campanha.',
        charQuantity: this.bestProductCharQuantity,
      }
      return [
        { clipboardText: `[primeiro ${label}`, text: `[primeiro ${label}`, ...variableInfo },
        { clipboardText: `[segundo ${label}`, text: `[segundo ${label}`, ...variableInfo },
        { clipboardText: `[terceiro ${label}`, text: `[terceiro ${label}`, ...variableInfo },
      ]
    },
    builderEmailVariables() {
      const excludedVariables = [
        '{fim validade}',
        '{inicio validade}',
        '{nome do combo}',
        '{nome produto}',
        '{preço 1}',
        '{preço 2}',
        '{desconto}',
        '{leve}',
        '{pague}',
        'primeiro melhor produto ',
        'segundo melhor produto ',
        'terceiro melhor produto ',
        'primeiro melhor combo ',
        'segundo melhor combo ',
        'terceiro melhor combo ',
        'Melhor produto',
        'Melhor combo',
      ]
      return this.variables
        .filter((item) => !excludedVariables.includes(item.clipboardText) && !['Melhor produto', 'Melhor combo'].includes(item.text))
        .concat(this.bestItemsVariables)
    },
    options() {
      return {
        translations: {
          'pt-BR': {
            'labels.merge_tags': 'Variáveis',
            missing_component_placeholder_text: 'Este componente não está disponível',
          },
          'en-US': {
            'labels.merge_tags': 'Variables',
            missing_component_placeholder_text: 'This component is not available',
          },
        },
        mergeTags: this.builderEmailVariables.reduce((acc, variable) => {
          return {
            [variable.clipboardText]: {
              value: variable.clipboardText,
              name: variable.text,
              sample: variable.example,
            },
            ...acc,
          }
        }, {}),
        ...((this.productsWithImage > 0 || this.campaignData?.auto_distributed === true) &&
          (() => {
            if (this.campaignData?.auto_distributed === true) {
              return {
                customJS: [
                  getAutoDistributedOfferCardCustomJS(
                    this.defaultColors,
                    Math.min(this.productsWithImage || 0, 10),
                    this.message,
                    this.modelOptions,
                    this.defaultModelValue,
                    this.modelQuantityMap
                  ),
                ],
              }
            } else {
              return {
                customJS: [getOfferCardCustomJS(this.defaultColors, Math.min(this.productsWithImage || 0, 10), this.message)],
              }
            }
          })()),
      }
    },
  },
  created() {
    if (!isEmpty(this.message?.builder_email_configuration)) {
      this.localBuilderConfiguration = this.message?.builder_email_configuration
    }
    this.productsWithImage = this.validateAllProductsImages()
  },
  beforeUnmount() {
    if (this.offerCardUpdateTimer) {
      clearTimeout(this.offerCardUpdateTimer)
      this.offerCardUpdateTimer = null
    }
    if (this.configureOfferCardsTimeout) {
      clearTimeout(this.configureOfferCardsTimeout)
      this.configureOfferCardsTimeout = null
    }
    this.isConfiguringOfferCards = false
  },
  methods: {
    postTemplateLoadFlag(active, ttlMs = 5000) {
      try {
        const iframe =
          document.querySelector('iframe[src*="unlayer"]') ||
          document.querySelector('iframe[id*="unlayer"]') ||
          document.querySelector('.unlayer-editor iframe') ||
          document.querySelector('iframe')

        if (iframe && iframe.contentWindow && iframe.contentWindow.postMessage) {
          iframe.contentWindow.postMessage(
            {
              type: 'offerCardTemplateLoad',
              source: 'parent-window',
              active: !!active,
              ttlMs,
            },
            '*'
          )
        }
      } catch (e) {}
    },
    ensureGlobalState() {
      if (!this.offerCardGlobalColors || typeof this.offerCardGlobalColors !== 'object') {
        this.offerCardGlobalColors = { mecanicaColor: null, descontoColor: null }
      }
      if (typeof this.offerCardGlobalFont === 'undefined') {
        this.offerCardGlobalFont = null
      }
      if (!this.offerCardKnownIds || !(this.offerCardKnownIds instanceof Set)) {
        this.offerCardKnownIds = new Set()
      }
      if (!this.offerCardLastState || !(this.offerCardLastState instanceof Map)) {
        this.offerCardLastState = new Map()
      }
    },

    setGlobalColorsInDesign(design, colors) {
      if (!design) return
      design.body = design.body || {}
      design.body.values = design.body.values || {}
      design.body.values._meta = design.body.values._meta || {}
      const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
      const mec = colors?.mecanicaColor && HEX.test(colors.mecanicaColor) ? colors.mecanicaColor : null
      const des = colors?.descontoColor && HEX.test(colors.descontoColor) ? colors.descontoColor : null
      design.body.values._meta.offerCardGlobalColors = { mecanicaColor: mec, descontoColor: des }
    },
    getGlobalColorsFromDesign(design) {
      return design?.body?.values?._meta?.offerCardGlobalColors || null
    },
    setGlobalFontInDesign(design, font) {
      if (!design) return
      design.body = design.body || {}
      design.body.values = design.body.values || {}
      design.body.values._meta = design.body.values._meta || {}
      const ok = font && typeof font === 'object' && typeof font.value === 'string'
      design.body.values._meta.offerCardGlobalFont = ok ? { label: font.label || 'Custom', value: font.value } : null
    },
    getGlobalFontFromDesign(design) {
      return design?.body?.values?._meta?.offerCardGlobalFont || null
    },
    scanOfferCards(node, acc = [], path = 'body') {
      if (!node || typeof node !== 'object') return acc
      const isOfferCard = (content) => {
        if (!content) return false
        if (content.type === 'custom' && content.slug === 'offer_card') return true
        const htmlClass = content.values?._meta?.htmlClassNames || ''
        const htmlID = content.values?._meta?.htmlID || ''
        if (typeof htmlClass === 'string' && htmlClass.includes('u_content_custom_offer_card')) return true
        if (typeof htmlID === 'string' && htmlID.includes('u_content_custom_offer_card')) return true
        if (content.values?._meta?.customToolName === 'offer_card') return true
        return false
      }
      if ('type' in node || 'slug' in node || 'values' in node) {
        if (isOfferCard(node)) acc.push(node)
      }
      const dive = (child, label) => this.scanOfferCards(child, acc, `${path}.${label}`)
      if (Array.isArray(node.rows)) node.rows.forEach((r, i) => dive(r, `rows[${i}]`))
      if (Array.isArray(node.columns)) node.columns.forEach((c, i) => dive(c, `columns[${i}]`))
      if (Array.isArray(node.contents)) node.contents.forEach((c, i) => dive(c, `contents[${i}]`))
      Object.keys(node).forEach((k) => {
        const v = node[k]
        if (v && typeof v === 'object' && (v.rows || v.columns || v.contents)) dive(v, k)
      })
      return acc
    },
    extractOfferState(c) {
      const v = (c && c.values) || {}
      return {
        mecanica: v.mecanicaOfferColor || null,
        desconto: v.descontoColor || null,
        font: v.fontFamily && typeof v.fontFamily === 'object' ? v.fontFamily.value : null,
      }
    },
    stateToKey(s) {
      return `${s.mecanica || ''}|${s.desconto || ''}|${s.font || ''}`
    },
    isOfferCardContent(content) {
      if (!content) return false
      if (content.type === 'custom' && content.slug === 'offer_card') return true

      const v = content.values || {}
      const htmlClass = v._meta?.htmlClassNames || ''
      const htmlID = v._meta?.htmlID || ''

      if (typeof htmlClass === 'string' && htmlClass.includes('u_content_custom_offer_card')) return true
      if (typeof htmlID === 'string' && htmlID.includes('u_content_custom_offer_card')) return true
      if (v._meta?.customToolName === 'offer_card') return true

      return false
    },
    createWarningTextContent(originalContent, message) {
      const padding = originalContent?.values?.containerPadding || '16px'
      return {
        type: 'text',
        values: {
          _meta: {
            ...(originalContent?.values?._meta || {}),
            replacedFrom: 'offer_card',
          },
          containerPadding: padding,
          text: `<div id="not_allowed" style="border:2px dashed red;color:red;padding:16px;text-align:center;font-family:sans-serif;font-size:14px;">
            ${message || 'A configuração atual da oferta não permite o uso deste componente.'}
          </div>`,
        },
      }
    },
    replaceOfferCardsWithWarning(design, message) {
      if (!design || !design.body) return design

      const clone = JSON.parse(JSON.stringify(design))

      const walk = (node) => {
        if (!node || typeof node !== 'object') return node

        if (Array.isArray(node.contents)) {
          node.contents = node.contents.map((c) => {
            if (this.isOfferCardContent(c)) {
              return this.createWarningTextContent(c, message)
            }
            return walk(c)
          })
        }

        if (Array.isArray(node.rows)) node.rows = node.rows.map((r) => walk(r))
        if (Array.isArray(node.columns)) node.columns = node.columns.map((c) => walk(c))

        Object.keys(node).forEach((k) => {
          const v = node[k]
          if (v && typeof v === 'object' && (v.rows || v.columns || v.contents)) {
            node[k] = walk(v)
          }
        })

        return node
      }

      clone.body = walk(clone.body)

      if (clone.counters && typeof clone.counters === 'object') {
        clone.counters.u_content_custom_offer_card = 0
      }

      return clone
    },
    configureOfferCardsWithSavedOrder() {
      if (this.configureOfferCardsTimeout) {
        clearTimeout(this.configureOfferCardsTimeout)
        this.configureOfferCardsTimeout = null
      }

      if (this.isConfiguringOfferCards) {
        return
      }

      if (isEditMode(this.message)) {
        const extractFunction = this.campaignData?.auto_distributed === true ? extractAutoDistributedBestOfferOrder : extractBestOfferOrder

        const bestOfferOrder = extractFunction(this.message?.builder_email_configuration)

        if (bestOfferOrder && bestOfferOrder.length > 0) {
          this.isConfiguringOfferCards = true

          const findUnlayerIframe = () => {
            return (
              document.querySelector('iframe[src*="unlayer"]') ||
              document.querySelector('iframe[id*="unlayer"]') ||
              document.querySelector('.unlayer-editor iframe') ||
              document.querySelector('iframe')
            )
          }

          const totalCards = bestOfferOrder.length
          const maxDelay = totalCards > 0 ? (totalCards - 1) * 300 : 0
          const totalTimeNeeded = maxDelay + 1000

          this.configureOfferCardsTimeout = setTimeout(() => {
            const iframe = findUnlayerIframe()
            if (!iframe) {
              this.isConfiguringOfferCards = false
              return
            }

            bestOfferOrder.forEach((order, index) => {
              if (order.htmlID) {
                const model = order.model || null
                const bestOffer = order.bestOffer ? order.bestOffer.toString() : null

                setTimeout(() => {
                  try {
                    iframe.contentWindow.postMessage(
                      {
                        type: 'configureOfferCard',
                        source: 'parent-window',
                        cardId: order.htmlID,
                        offerNumber: bestOffer,
                        model: model,
                      },
                      '*'
                    )
                  } catch (e) {
                    console.error('[configureOfferCardsWithSavedOrder] Erro ao enviar postMessage:', e)
                  }
                }, index * 300)
              }
            })

            setTimeout(() => {
              this.isConfiguringOfferCards = false
              this.configureOfferCardsTimeout = null
            }, totalTimeNeeded)
          }, 2000)
        } else {
          this.isConfiguringOfferCards = false
        }
      } else {
        this.isConfiguringOfferCards = false
      }
    },

    updateBestOfferDropdownOptions(model) {
      if (this.campaignData?.auto_distributed !== true) {
        return
      }

      const quantity = this.modelQuantityMap?.[model] || Math.min(this.productsWithImage || 0, 10)
      const modelNameMap = {
        repurchase: 'Recompra',
        recovery: 'Resgate',
        activation: 'Ativação',
      }
      const modelName = modelNameMap[model] || 'Ativação'

      const bestOfferPanel = document.querySelector('.bestOffer_panel, [data-property="bestOffer"]')
      if (!bestOfferPanel) {
        return
      }

      const dropdownMenu = bestOfferPanel.querySelector('.dropdown-menu, [role="listbox"], .unlayer-dropdown-menu, .blockbuilder-dropdown-menu')
      if (dropdownMenu) {
        const items = dropdownMenu.querySelectorAll('.dropdown-item, [role="option"], .blockbuilder-dropdown-item')
        items.forEach((item) => {
          const itemText = item.textContent || item.innerText || ''
          const numberMatch = itemText.match(/(\d+)/)
          const itemNum = numberMatch ? parseInt(numberMatch[1], 10) : null

          if (itemNum && itemNum > quantity) {
            item.style.display = 'none'
            item.style.visibility = 'hidden'
          } else {
            item.style.display = ''
            item.style.visibility = ''
          }
        })
      }

      const dropdownButton = bestOfferPanel.querySelector('button[role="combobox"], .dropdown-button, .blockbuilder-dropdown-button')
      if (dropdownButton) {
        const currentValue = dropdownButton.getAttribute('aria-label') || dropdownButton.textContent || ''
        const currentNumber = parseInt(currentValue.replace(/[^0-9]/g, ''), 10)

        if (isNaN(currentNumber) || currentNumber < 1 || currentNumber > quantity) {
          const newValue = `1ª Melhor Oferta – ${modelName}`
          dropdownButton.setAttribute('aria-label', newValue)
          const labelSpan = dropdownButton.querySelector('.label, .blockbuilder-label')
          if (labelSpan) {
            labelSpan.textContent = newValue
          }
        }
      }
    },
    setupBestOfferDropdownFilter() {
      if (this.campaignData?.auto_distributed !== true) {
        return
      }

      let postMessageTimeout = null
      let lastProcessedModel = null
      let lastProcessedTime = 0

      const handlePostMessage = (event) => {
        if (event.data && event.data.type === 'offerCardModelChange' && event.data.source === 'unlayer-iframe') {
          if (event.data.detail && event.data.detail.model) {
            const currentModel = event.data.detail.model
            const currentTime = Date.now()

            if (lastProcessedModel === currentModel && currentTime - lastProcessedTime < 500) {
              return
            }

            if (postMessageTimeout) {
              clearTimeout(postMessageTimeout)
            }

            postMessageTimeout = setTimeout(() => {
              lastProcessedModel = currentModel
              lastProcessedTime = Date.now()
              this.updateBestOfferDropdownOptions(currentModel)
              postMessageTimeout = null
            }, 300)
          }
        }
      }

      window.addEventListener('message', handlePostMessage)

      this.$once('hook:beforeUnmount', () => {
        window.removeEventListener('message', handlePostMessage)
      })
    },
    editorLoaded() {
      const editor = this.$refs.emailEditor?.editor
      if (!editor) {
        return
      }

      if (this.editorListenersSetup) {
        if (this.localBuilderConfiguration && !this.isConfiguringOfferCards) {
          this.configureOfferCardsWithSavedOrder()
        }
        return
      }

      this.editorListenersSetup = true
      this.ensureGlobalState()
      window.maxOfferCards = this.productsWithImage

      if (this.campaignData?.auto_distributed === true) {
        setTimeout(() => {
          this.setupBestOfferDropdownFilter()
        }, 1000)
      }

      editor.addEventListener('design:loaded', () => {
        editor.saveDesign((design) => {
          this.ensureGlobalState()

          const savedColors = this.getGlobalColorsFromDesign(design)
          if (savedColors) {
            if (savedColors.mecanicaColor) this.offerCardGlobalColors.mecanicaColor = savedColors.mecanicaColor
            if (savedColors.descontoColor) this.offerCardGlobalColors.descontoColor = savedColors.descontoColor
          }
          const savedFont = this.getGlobalFontFromDesign(design)
          if (savedFont && typeof savedFont.value === 'string') {
            this.offerCardGlobalFont = savedFont
          }

          const found = this.scanOfferCards(design?.body)
          const first = found?.[0]
          if (first?.values) {
            if (!savedColors) {
              this.offerCardGlobalColors.mecanicaColor = first.values.mecanicaOfferColor ?? this.offerCardGlobalColors.mecanicaColor
              this.offerCardGlobalColors.descontoColor = first.values.descontoColor ?? this.offerCardGlobalColors.descontoColor
            }
            if (!savedFont && first.values.fontFamily?.value) {
              this.offerCardGlobalFont = first.values.fontFamily
            }
          }

          this.offerCardKnownIds = new Set()
          this.offerCardLastState = new Map()
          const all = this.scanOfferCards(design?.body)
          all.forEach((c) => {
            const id = c.values?._meta?.htmlID || c.id || c.values?._meta?.htmlClassNames
            if (!id) return
            this.offerCardKnownIds.add(id)
            this.offerCardLastState.set(id, this.stateToKey(this.extractOfferState(c)))
          })
        })
      })

      editor.addEventListener('design:updated', () => {
        clearTimeout(this.offerCardUpdateTimer)
        this.offerCardUpdateTimer = setTimeout(() => {
          editor.saveDesign((design) => {
            if (!design?.body) return
            this.ensureGlobalState()

            const cards = this.scanOfferCards(design.body)
            let changedByUser = false
            let changed = false
            const newIds = []

            for (const c of cards) {
              const id = c.values?._meta?.htmlID || c.id || c.values?._meta?.htmlClassNames
              if (!id) continue
              c.values = c.values && typeof c.values === 'object' ? c.values : {}

              const cur = this.extractOfferState(c)
              const curKey = this.stateToKey(cur)
              const prevKey = this.offerCardLastState.get(id)

              if (!this.offerCardKnownIds.has(id)) {
                newIds.push(id)
              }

              if (prevKey && prevKey !== curKey) {
                if (cur.mecanica) this.offerCardGlobalColors.mecanicaColor = cur.mecanica
                if (cur.desconto) this.offerCardGlobalColors.descontoColor = cur.desconto
                if (cur.font) this.offerCardGlobalFont = { label: 'Custom', value: cur.font }
                changedByUser = true
              }
            }

            const mec = this.offerCardGlobalColors?.mecanicaColor || null
            const des = this.offerCardGlobalColors?.descontoColor || null
            const fnt = this.offerCardGlobalFont && typeof this.offerCardGlobalFont.value === 'string' ? this.offerCardGlobalFont : null

            if (changedByUser) {
              cards.forEach((c) => {
                const before = JSON.stringify(c.values || {})
                c.values = c.values && typeof c.values === 'object' ? c.values : {}
                if (mec) c.values.mecanicaOfferColor = mec
                if (des) c.values.descontoColor = des
                if (fnt) c.values.fontFamily = fnt
                if (JSON.stringify(c.values) !== before) changed = true
              })
            }

            if (!changedByUser && (mec || des || fnt) && newIds.length) {
              cards.forEach((c) => {
                const id = c.values?._meta?.htmlID || c.id || c.values?._meta?.htmlClassNames
                if (!id || !newIds.includes(id)) return
                const before = JSON.stringify(c.values || {})
                c.values = c.values && typeof c.values === 'object' ? c.values : {}
                if (mec) c.values.mecanicaOfferColor = mec
                if (des) c.values.descontoColor = des
                if (fnt) c.values.fontFamily = fnt
                if (JSON.stringify(c.values) !== before) changed = true
              })
            }

            if (changed) {
              this.setGlobalColorsInDesign(design, this.offerCardGlobalColors)
              this.setGlobalFontInDesign(design, this.offerCardGlobalFont)
              editor.loadDesign(design)
            }

            for (const c of cards) {
              const id = c.values?._meta?.htmlID || c.id || c.values?._meta?.htmlClassNames
              if (!id) continue
              this.offerCardKnownIds.add(id)
              this.offerCardLastState.set(id, this.stateToKey(this.extractOfferState(c)))
            }
          })
        }, 250)
      })

      if (this.localBuilderConfiguration) {
        const cfg =
          (this.productsWithImage || 0) === 0 && this.campaignData?.auto_distributed !== true
            ? this.replaceOfferCardsWithWarning({ ...this.localBuilderConfiguration }, 'A configuração atual da oferta não permite o uso deste componente.')
            : this.localBuilderConfiguration

        setTimeout(() => {
          editor.loadDesign(cfg)
          setTimeout(() => {
            if (!this.isConfiguringOfferCards) {
              this.configureOfferCardsWithSavedOrder()
            }
          }, 500)
        }, 100)
      }
    },

    async exportDesign() {
      return new Promise((resolve) => this.$refs.emailEditor.editor.saveDesign(resolve))
    },
    async exportHtml() {
      return new Promise((resolve, reject) => {
        try {
          this.$refs.emailEditor.editor.exportHtml((data) => resolve(data.html))
        } catch (error) {
          reject(error)
        }
      })
    },
    async getConfiguration() {
      const processedHtml = await this.exportHtml()
      const builderEmailConfiguration = await this.exportDesign()

      if (this.campaignData?.auto_distributed === true) {
        const amountBestOffersModels = this.calculateAmountBestOffersModels(processedHtml)
        return {
          processed_html: processedHtml,
          builder_email_configuration: builderEmailConfiguration,
          amount_best_offers_models: amountBestOffersModels,
        }
      } else {
        const amountBestOffer = this.calculateAmountBestOffer(processedHtml)
        return {
          processed_html: processedHtml,
          builder_email_configuration: builderEmailConfiguration,
          amount_best_offers: amountBestOffer,
        }
      }
    },
    calculateAmountBestOffer(processedHtml = null) {
      if (!processedHtml) {
        return null
      }

      const hasOfferCardClasses = processedHtml.includes('offer-card-small-container') || processedHtml.includes('offer-card-container')
      if (!hasOfferCardClasses) {
        return null
      }

      return Math.min(this.productsWithImage || 0, 10)
    },
    calculateAmountBestOffersModels(processedHtml = null) {
      if (!processedHtml) {
        return []
      }

      const hasOfferCardClasses = processedHtml.includes('offer-card-small-container') || processedHtml.includes('offer-card-container')
      if (!hasOfferCardClasses) {
        return []
      }

      const ariaOfferRegex = /aria-offer\s*=\s*["']best-offer-([a-z]+)-(\d+)["']/gi
      const modelMap = {}

      const modelNameMap = {
        repurchase: 'REPURCHASE',
        recovery: 'RECOVERY',
        activation: 'ACTIVATION',
      }

      const matches = Array.from(processedHtml.matchAll(ariaOfferRegex))

      matches.forEach((match) => {
        const modelFromHtml = match[1]
        const index = parseInt(match[2], 10)

        if (isNaN(index) || index < 1) {
          return
        }

        let model = modelNameMap[modelFromHtml]
        if (!model) {
          model = modelFromHtml.toUpperCase()
        }

        if (!model) {
          return
        }

        if (!modelMap[model]) {
          modelMap[model] = 0
        }

        if (index > modelMap[model]) {
          modelMap[model] = index
        }
      })

      const amountBestOffersModels = Object.entries(modelMap).map(([model, quantity]) => {
        return {
          model: model,
          quantity: quantity,
        }
      })

      return amountBestOffersModels
    },
    async templateGetter() {
      const configuration = await this.exportDesign()
      return configuration
    },
    extractModelsFromDesign(design) {
      if (!design || !design.body) return new Set()

      const models = new Set()
      const offerCards = this.scanOfferCards(design.body)

      offerCards.forEach((card) => {
        const modelValue = card.values?.model
        if (modelValue) {
          let model = typeof modelValue === 'string' ? modelValue : modelValue.value || modelValue

          if (model) {
            models.add(model)
          }
        }
      })

      return models
    },
    validateTemplateModels(templateDesign) {
      if (this.campaignData?.auto_distributed !== true) {
        return { valid: true }
      }

      const templateModels = this.extractModelsFromDesign(templateDesign)
      if (templateModels.size === 0) {
        return { valid: true }
      }

      const availableModels = new Set(this.modelOptions.map((opt) => opt.value))
      const missingModels = []

      templateModels.forEach((model) => {
        if (!availableModels.has(model)) {
          const modelNameMap = {
            repurchase: 'Recompra',
            recovery: 'Resgate',
            activation: 'Ativação',
          }
          missingModels.push(modelNameMap[model] || model)
        }
      })

      if (missingModels.length > 0) {
        return {
          valid: false,
          missingModels,
        }
      }

      return { valid: true }
    },
    getMaxBestOfferForModel(model) {
      if (this.campaignData?.auto_distributed === true && model) {
        const quantity = this.modelQuantityMap?.[model] || 0
        const max = Math.min(quantity, 10)
        return max
      }
      const max = Math.min(this.productsWithImage || 0, 10)
      return max
    },
    normalizeBestOfferValues(design) {
      if (!design || !design.body) {
        return design
      }

      const clone = JSON.parse(JSON.stringify(design))
      const offerCards = this.scanOfferCards(clone.body)

      offerCards.forEach((card, index) => {
        if (!card.values) card.values = {}

        if (this.campaignData?.auto_distributed === true && !card.values.model) {
          card.values.model = this.defaultModelValue
        }

        const currentBestOffer = parseInt(card.values.bestOffer || '1', 10)
        let maxBestOffer = 1

        if (this.campaignData?.auto_distributed === true) {
          const model = card.values.model || this.defaultModelValue
          maxBestOffer = this.getMaxBestOfferForModel(model)
        } else {
          maxBestOffer = Math.min(this.productsWithImage || 0, 10)
        }

        const normalizedBestOffer = maxBestOffer > 0 ? Math.min(currentBestOffer, maxBestOffer) : 1

        card.values.bestOffer = normalizedBestOffer.toString()

        const modelNameMap = {
          repurchase: 'Recompra',
          recovery: 'Resgate',
          activation: 'Ativação',
        }
        const model = card.values.model || this.defaultModelValue
        const modelName = modelNameMap[model] || 'Ativação'
        card.values['nome da oferta'] = `${normalizedBestOffer}ª Melhor Oferta${this.campaignData?.auto_distributed === true ? ' – ' + modelName : ''}`
      })
      return clone
    },
    configureTemplateOfferCards() {
      const editor = this.$refs.emailEditor?.editor
      if (!editor) {
        return
      }
      editor.saveDesign((design) => {
        const extractFunction = this.campaignData?.auto_distributed === true ? extractAutoDistributedBestOfferOrder : extractBestOfferOrder
        const bestOfferOrder = extractFunction(design)

        if (bestOfferOrder && bestOfferOrder.length > 0 && window.configureOfferCard) {
          setTimeout(() => {
            bestOfferOrder.forEach((order, index) => {
              if (order.htmlID && window.configureOfferCard) {
                window.configureOfferCard(order.htmlID, order.bestOffer)
              }
            })
          }, 1000)
        }
      })
    },
    templateSetter(emailTemplate) {
      this.offerCardKnownIds = new Set()
      this.offerCardLastState = new Map()
      let cfg = emailTemplate.configuration

      const validation = this.validateTemplateModels(cfg)
      if (!validation.valid) {
        const modelsList = validation.missingModels.join(', ')
        this.$snackbar({
          message: `O template contém modelos que não estão disponíveis na sua oferta atual: ${modelsList}. Não é possível carregar este template.`,
          snackbarColor: 'error',
        })
        return
      }

      if ((this.productsWithImage || 0) === 0 && this.campaignData?.auto_distributed !== true) {
        cfg = this.replaceOfferCardsWithWarning(cfg, 'A configuração atual da oferta não permite o uso do componente "Melhor oferta" no corpo do e-mail.')
      } else {
        cfg = this.normalizeBestOfferValues(cfg)
      }

      const editor = this.$refs.emailEditor.editor

      const onDesignLoaded = () => {
        setTimeout(() => {
          this.postTemplateLoadFlag(true, 8000)
          this.configureTemplateOfferCards()
          this.postTemplateLoadFlag(false)
          editor.removeEventListener('design:loaded', onDesignLoaded)
        }, 1500)
      }

      editor.addEventListener('design:loaded', onDesignLoaded)
      this.postTemplateLoadFlag(true, 8000)
      editor.loadDesign(cfg)
    },
    async validInput() {
      const design = await this.exportDesign()
      if (!design || Object.keys(design).length === 0) return false

      const modelValidation = this.validateTemplateModels(design)
      if (!modelValidation.valid) {
        const modelsList = modelValidation.missingModels.join(', ')
        this.$snackbar({
          message: `O template contém modelos que não estão disponíveis na sua oferta atual: ${modelsList}. Não é possível salvar.`,
          snackbarColor: 'error',
        })
        return false
      }

      const hasOfferCardInDesign = design.counters && design.counters.u_content_custom_offer_card > 0
      if (hasOfferCardInDesign) {
        const productsWithImage = this.validateAllProductsImages()
        if (productsWithImage === 0 && this.campaignData?.auto_distributed !== true) {
          this.$snackbar({
            message: 'A configuração atual da oferta não permite o uso do componente "Melhor oferta" no corpo do e-mail.',
            snackbarColor: 'error',
          })
          return false
        }
      }
      const hasNotAllowedElement = await this.checkForNotAllowedElementInHTML()
      if (hasNotAllowedElement) {
        this.$snackbar({
          message: 'A configuração atual da oferta não permite o uso do componente "Melhor oferta" no corpo do e-mail.',
          snackbarColor: 'error',
        })
        return false
      }
      return true
    },
    isTouched() {
      return true
    },
    async checkForNotAllowedElementInHTML() {
      try {
        const htmlContent = await this.exportHtml()
        if (!htmlContent) {
          return false
        }
        const hasNotAllowed = htmlContent.includes('id="not_allowed"')
        return hasNotAllowed
      } catch (error) {
        return false
      }
    },
  },
}
</script>

<style lang="scss">
.unlayer-editor {
  height: 1000px;
}
</style>
